import React, { useCallback, useMemo } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import type { UvInsertCartLineItem } from '@shared/view/uv-inserts/build-cart-line-item';
import { usePage } from '../context/PageContext';
import { FlowBridgeContext } from './context/FlowBridgeContext';
import { createUvInsertsRouter } from './UvInsertsFlow.vm';

type UvInsertsFlowProps = {
  /** Passed to Header's close button — closes the gift wrap modal dialog. */
  onClose: (e?: React.MouseEvent) => void;
};

/**
 * Entry component for the UV inserts sub-flow.
 * Mounts a TanStack memory router (fresh per mount = fresh per modal open).
 * Resolved config and flow state live on UvInsertsContext at the app root.
 *
 * Architecture:
 *   UvInsertsFlow
 *     FlowBridgeContext.Provider  (outer callbacks — onFinish, onBack, onClose)
 *     RouterProvider              (TanStack memory router; UvInsertsContext is at app root)
 *       RootLayout (from router.tsx)
 *         UvInsertsNavContextProvider  (PageContext.Provider override — Header/Footer/OptionSelectionPage)
 *           Header
 *           <Outlet />
 *           Footer
 */
export function UvInsertsFlow({ onClose }: UvInsertsFlowProps) {
  // Read from OUTER PageContext (before the RouterProvider overrides it).
  const { goToPage: outerGoToPage } = usePage();

  /**
   * Called by UvInsertsNavContext.goToPage('FINISHED').
   * Receives the MVVM cart item builder (a closure over current UvInsertsContext state)
   * and threads it into the outer PageContext as a pre-built item, bypassing
   * visualiserCartService and window.giftWrapData entirely for UV inserts.
   */
  const onFinish = useCallback(
    (uvInsertCartItemBuilder: (() => UvInsertCartLineItem | null) | null) => {
      // Store the builder on the outer context before navigating so
      // PageContext.handleFinished can retrieve it.
      outerGoToPage('FINISHED', uvInsertCartItemBuilder);
    },
    [outerGoToPage],
  );

  const onBack = useCallback(() => {
    outerGoToPage('firstPage');
  }, [outerGoToPage]);

  // Create router once per mount — resets to /addPersonalTouch each time the modal opens.
  const router = useMemo(() => createUvInsertsRouter(), []);

  return (
    <FlowBridgeContext.Provider value={{ onFinish, onBack, onClose }}>
      <RouterProvider router={router} />
    </FlowBridgeContext.Provider>
  );
}
